// src/pages/markets/board/api/metrics.ts
//
// Registry of metric descriptors. The keys are the sort keys the backend
// accepts (_SORT_EXPRESSIONS in api_screener_board.py); the kernels must
// compute the same number the backend's ORDER BY does, or the card shows a
// value the column did not rank on.
//
// The sort dropdown is rendered from schema.sort_options, not from this file.
// A key the schema offers but this registry does not know still works — see
// fallbackMetric().

export type MetricFormat = 'usd' | 'prob' | 'prob_diff' | 'prob_points' | 'ratio' | 'count' | 'number';

export type MetricKernel =
    | { kind: 'field'; path: string }
    | { kind: 'sum'; of: MetricKernel[] }
    | { kind: 'max'; of: MetricKernel[] }
    | { kind: 'min'; of: MetricKernel[] }
    | { kind: 'diff'; a: MetricKernel; b: MetricKernel }
    | { kind: 'ratio'; a: MetricKernel; b: MetricKernel }
    | { kind: 'abs'; of: MetricKernel };

export interface MetricDef {
    key: string;
    label: string;
    group: string;
    format: MetricFormat;
    signed: boolean;
    sortable: boolean;
    kernel: MetricKernel | null;
}

function f(path: string): MetricKernel {
    return { kind: 'field', path };
}

export const METRIC_REGISTRY: Record<string, MetricDef> = {
    dominant_capital: {
        key: 'dominant_capital',
        label: 'Dominant capital',
        group: 'Capital',
        format: 'usd',
        signed: false,
        sortable: true,
        kernel: { kind: 'max', of: [f('capital_usd_yes'), f('capital_usd_no')] },
    },
    // Backend key stays implied_disparity; the display name is Implied
    // Disparity everywhere. ("Prob disparity" is retired.)
    implied_disparity: {
        key: 'implied_disparity',
        label: 'Implied Disparity',
        group: 'Edge',
        format: 'prob_diff',
        signed: true,
        sortable: true,
        kernel: { kind: 'diff', a: f('raw_implied'), b: f('market_prob') },
    },
    imbalance: {
        key: 'imbalance',
        label: 'Edge imbalance',
        group: 'Edge',
        format: 'prob_points',
        signed: false,
        sortable: true,
        kernel: {
            kind: 'abs',
            of: { kind: 'diff', a: f('agg.avg_edge_yes'), b: f('agg.avg_edge_no') },
        },
    },
    trader_count: {
        key: 'trader_count',
        label: 'Traders',
        group: 'Traders',
        format: 'count',
        signed: false,
        sortable: false,
        kernel: { kind: 'sum', of: [f('n_traders_yes'), f('n_traders_no')] },
    },
    implied_prob: {
        key: 'implied_prob',
        label: 'Implied',
        group: 'Edge',
        format: 'prob',
        signed: false,
        sortable: false,
        kernel: f('raw_implied'),
    },
    market_prob: {
        key: 'market_prob',
        label: 'Market',
        group: 'Edge',
        format: 'prob',
        signed: false,
        sortable: false,
        kernel: f('market_prob'),
    },
    capital_yes: {
        key: 'capital_yes',
        label: 'Capital YES',
        group: 'Capital',
        format: 'usd',
        signed: false,
        sortable: false,
        kernel: f('capital_usd_yes'),
    },
    capital_no: {
        key: 'capital_no',
        label: 'Capital NO',
        group: 'Capital',
        format: 'usd',
        signed: false,
        sortable: false,
        kernel: f('capital_usd_no'),
    },
    created_at: {
        key: 'created_at',
        label: 'Created',
        group: 'Event',
        format: 'number',
        signed: false,
        sortable: true,
        kernel: null,
    },
    prob_jump: {
        key: 'prob_jump',
        label: 'Jump',
        group: 'Activity',
        format: 'number',
        signed: false,
        sortable: true,
        kernel: null,
    },
    prob_drift: {
        key: 'prob_drift',
        label: 'Drift',
        group: 'Activity',
        format: 'number',
        signed: false,
        sortable: true,
        kernel: null,
    },
    volume: {
        key: 'volume',
        label: 'Volume',
        group: 'Activity',
        format: 'usd',
        signed: false,
        sortable: true,
        kernel: null,
    },
    volume_change: {
        key: 'volume_change',
        label: 'Volume change',
        group: 'Activity',
        format: 'ratio',
        signed: false,
        sortable: true,
        kernel: null,
    },
};

function humanize(key: string): string {
    const s = key.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// A sort key the schema offers but the registry does not know: it can still be
// selected and ranked, only its per-card value comes from event.sort_value.
export function fallbackMetric(key: string): MetricDef {
    return {
        key,
        label: humanize(key),
        group: 'Other',
        format: 'number',
        signed: false,
        sortable: true,
        kernel: null,
    };
}

export function getMetric(key: string): MetricDef {
    return METRIC_REGISTRY[key] ?? fallbackMetric(key);
}

export function getMetrics(keys: readonly string[]): MetricDef[] {
    return keys.map(getMetric);
}